import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Users, MapPin, Calendar, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const HagafPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/research" 
            className="inline-flex items-center text-indigo-200 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Research
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center gap-6 mb-6">
              <img 
                src="https://customer-assets-0z36b82j.emergentagent.net/job_7a4f11f8-6c06-4e1c-b433-73d9453c195e/artifacts/ks29hq79_HAGAF%20LOGO.jpg"
                alt="HAGAF Logo"
                className="w-32 h-32 object-contain bg-white rounded-lg p-2"
              />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-2">HAGAF</h1>
                <p className="text-xl text-indigo-200">
                  Histories of Asia's Gendered Armed Frontiers, 1780–2021
                </p>
              </div>
            </div>
            <p className="text-lg text-indigo-100 leading-relaxed">
              A five-year international research initiative exploring how war and militarisation 
              shape everyday life, family relationships, and community memories in Asia's armed frontiers.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Central Question */}
            <Card className="mb-12 border-l-4 border-indigo-600">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-indigo-900 mb-4 italic">
                  "What happens to everyday life, family relationships and community memories 
                  when war and militarisation become part of the landscape?"
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  These questions lie at the heart of <strong>Histories of Asia's Gendered Armed Frontiers, 1780–2021 (HAGAF)</strong>, 
                  a five-year international research initiative (2025–2030) led by Professor Mandy Sadan 
                  at the University of Warwick, with The Highland Institute among its partners.
                </p>
              </CardContent>
            </Card>

            {/* Project Details */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Project Duration</h3>
                  </div>
                  <p className="text-gray-600">2025–2030 (Five Years)</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Funding</h3>
                  </div>
                  <p className="text-gray-600">European Research Council (ERC) through the EU's Horizon Europe programme</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Lead Institution</h3>
                  </div>
                  <p className="text-gray-600">University of Warwick, led by Professor Mandy Sadan</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <MapPin className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Research Areas</h3>
                  </div>
                  <p className="text-gray-600">Bangladesh, India, and Myanmar</p>
                </CardContent>
              </Card>
            </div>

            {/* Research Approach */}
            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Research Approach</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                HAGAF explores communities in Bangladesh, India and Myanmar through historical and ethnographic research. 
                Working with local communities, researchers will draw on:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Oral histories and family records</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Photographs and visual materials</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Songs, rituals, and material culture</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Local archives</span>
                </li>
              </ul>
            </div>

            {/* Women's Stories */}
            <Card className="mb-12 bg-indigo-50 border-indigo-200">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-indigo-900 mb-4">
                  Centering Women's Voices
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Particular attention will be given to <strong>women's stories</strong>, which remain largely absent 
                  from written histories of the region's conflict zones. By bringing these voices and forms of local 
                  knowledge into the historical record, HAGAF aims to create more inclusive, ethically grounded 
                  and locally informed histories of Asia's armed frontiers.
                </p>
              </CardContent>
            </Card>

            {/* Highland Institute Partnership */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                The Highland Institute's Role
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Highland Institute is proud to be among the partners for this landmark research initiative. 
                Our expertise in community-based research, oral history documentation, and deep engagement with 
                highland communities in Northeast India positions us to contribute meaningfully to HAGAF's mission 
                of recovering and amplifying marginalised voices from Asia's armed frontiers.
              </p>
              <Link 
                to="/about"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Learn more about The Highland Institute
                <ExternalLink className="ml-2 w-4 h-4" />
              </Link>
            </div>

            {/* Back Link */}
            <div className="text-center">
              <Link 
                to="/research"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                View All Research Projects
              </Link>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default HagafPage;
